import {
  DEFAULT_SENSITIVE_HEADER_NAMES,
  DEFAULT_SENSITIVE_JSON_KEYS,
  REDACTED_LOG_VALUE,
} from '../constants/request';
import type {
  RequestTelemetryRedactionConfig,
  RequestTelemetryRedactionResolvedConfig,
  RequestTelemetryRedactionState,
} from '../types/request';

import {generateValidUrl} from './url';

const normalizeSensitiveKey = (key: string): string => {
  return key
    .toLowerCase()
    .replace(/\[\]/g, '')
    .replace(/[^a-z0-9]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_/, '')
    .replace(/_$/, '');
};

const normalizeConfiguredKeys = (values?: Iterable<string>): Set<string> => {
  if (!values) {
    return new Set();
  }

  return new Set(
    Array.from(values).flatMap((value) => {
      const normalized = normalizeSensitiveKey(value);

      return normalized ? [normalized] : [];
    }),
  );
};

const createDefaultRedactionState = (): RequestTelemetryRedactionState => {
  return {
    sensitiveHeaders: normalizeConfiguredKeys(DEFAULT_SENSITIVE_HEADER_NAMES),
    sensitiveKeys: normalizeConfiguredKeys(DEFAULT_SENSITIVE_JSON_KEYS),
    exposedHeaders: new Set(),
    exposedKeys: new Set(),
  };
};

let requestTelemetryRedactionState = createDefaultRedactionState();
let resolvedRedactionStateCache: RequestTelemetryRedactionState | undefined;

const getResolvedRedactionState = (): RequestTelemetryRedactionState => {
  if (resolvedRedactionStateCache) {
    return resolvedRedactionStateCache;
  }

  const sensitiveHeaders = new Set(
    requestTelemetryRedactionState.sensitiveHeaders,
  );
  const sensitiveKeys = new Set(requestTelemetryRedactionState.sensitiveKeys);

  requestTelemetryRedactionState.exposedHeaders.forEach((key) => {
    sensitiveHeaders.delete(key);
  });

  requestTelemetryRedactionState.exposedKeys.forEach((key) => {
    sensitiveKeys.delete(key);
  });

  resolvedRedactionStateCache = {
    sensitiveHeaders,
    sensitiveKeys,
    exposedHeaders: requestTelemetryRedactionState.exposedHeaders,
    exposedKeys: requestTelemetryRedactionState.exposedKeys,
  };

  return resolvedRedactionStateCache;
};

export const setRequestTelemetryRedactionConfig = (
  config: RequestTelemetryRedactionConfig,
): void => {
  resolvedRedactionStateCache = undefined;
  requestTelemetryRedactionState = {
    sensitiveHeaders: new Set([
      ...Array.from(normalizeConfiguredKeys(DEFAULT_SENSITIVE_HEADER_NAMES)),
      ...Array.from(normalizeConfiguredKeys(config.sensitiveHeaders)),
    ]),
    sensitiveKeys: new Set([
      ...Array.from(normalizeConfiguredKeys(DEFAULT_SENSITIVE_JSON_KEYS)),
      ...Array.from(normalizeConfiguredKeys(config.sensitiveKeys)),
    ]),
    exposedHeaders: normalizeConfiguredKeys(config.exposedHeaders),
    exposedKeys: normalizeConfiguredKeys(config.exposedKeys),
  };
};

export const resetRequestTelemetryRedactionConfig = (): void => {
  resolvedRedactionStateCache = undefined;
  requestTelemetryRedactionState = createDefaultRedactionState();
};

export const getRequestTelemetryRedactionConfig =
  (): RequestTelemetryRedactionResolvedConfig => {
    const resolvedState = getResolvedRedactionState();

    return {
      sensitiveHeaders: Array.from(resolvedState.sensitiveHeaders),
      sensitiveKeys: Array.from(resolvedState.sensitiveKeys),
      exposedHeaders: Array.from(resolvedState.exposedHeaders),
      exposedKeys: Array.from(resolvedState.exposedKeys),
    };
  };

export const isSensitiveHeaderName = (key: string): boolean => {
  return getResolvedRedactionState().sensitiveHeaders.has(
    normalizeSensitiveKey(key),
  );
};

const isSensitiveKey = (key: string): boolean => {
  const normalizedKey = normalizeSensitiveKey(key);
  const {sensitiveKeys} = getResolvedRedactionState();

  for (const sensitiveKey of Array.from(sensitiveKeys)) {
    if (
      normalizedKey === sensitiveKey ||
      normalizedKey.startsWith(`${sensitiveKey}_`) ||
      normalizedKey.endsWith(`_${sensitiveKey}`) ||
      normalizedKey.includes(`_${sensitiveKey}_`)
    ) {
      return true;
    }
  }

  return false;
};

const redactSensitiveFieldValue = (value: unknown): unknown => {
  return value === undefined || value === null ? value : REDACTED_LOG_VALUE;
};

const isBlobLike = (value: unknown): value is Blob => {
  return typeof Blob !== 'undefined' && value instanceof Blob;
};

const isFileLike = (value: unknown): value is File => {
  return typeof File !== 'undefined' && value instanceof File;
};

const isFormDataLike = (value: unknown): value is FormData => {
  return typeof FormData !== 'undefined' && value instanceof FormData;
};

const isURLSearchParamsLike = (value: unknown): value is URLSearchParams => {
  return (
    typeof URLSearchParams !== 'undefined' && value instanceof URLSearchParams
  );
};

const assignRecordValue = (
  record: Record<string, unknown>,
  key: string,
  value: unknown,
): void => {
  if (!(key in record)) {
    record[key] = value;

    return;
  }

  const previousValue = record[key];

  if (Array.isArray(previousValue)) {
    previousValue.push(value);

    return;
  }

  record[key] = [previousValue, value];
};

const summarizeBinaryValue = (value: Blob | File): Record<string, unknown> => {
  return {
    type: value.type || undefined,
    size: value.size,
    ...(isFileLike(value) ? {name: REDACTED_LOG_VALUE} : {}),
  };
};

const redactSensitiveValueInternal = (
  value: unknown,
  visited: WeakSet<object>,
): unknown => {
  if (value === null || value === undefined) {
    return value;
  }

  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (value instanceof URL) {
    return sanitizeUrlForLog(value);
  }

  if (isURLSearchParamsLike(value)) {
    const redactedParams: Record<string, unknown> = {};

    value.forEach((entryValue, key) => {
      assignRecordValue(
        redactedParams,
        key,
        isSensitiveKey(key) ? REDACTED_LOG_VALUE : entryValue,
      );
    });

    return redactedParams;
  }

  if (isFormDataLike(value)) {
    const redactedFormData: Record<string, unknown> = {};

    value.forEach((entryValue, key) => {
      const sanitizedEntryValue = isSensitiveKey(key)
        ? REDACTED_LOG_VALUE
        : redactSensitiveValueInternal(
            isBlobLike(entryValue)
              ? summarizeBinaryValue(entryValue)
              : entryValue,
            visited,
          );

      assignRecordValue(redactedFormData, key, sanitizedEntryValue);
    });

    return redactedFormData;
  }

  if (isBlobLike(value)) {
    return summarizeBinaryValue(value);
  }

  if (typeof value !== 'object') {
    return String(value);
  }

  if (visited.has(value)) {
    return '[Circular]';
  }

  visited.add(value);

  if (Array.isArray(value)) {
    return value.map((item) => redactSensitiveValueInternal(item, visited));
  }

  const redactedObject: Record<string, unknown> = {};

  for (const [key, entryValue] of Object.entries(value)) {
    redactedObject[key] = isSensitiveKey(key)
      ? redactSensitiveFieldValue(entryValue)
      : redactSensitiveValueInternal(entryValue, visited);
  }

  return redactedObject;
};

const toUrl = (value: string | URL): URL | undefined => {
  if (value instanceof URL) {
    return value;
  }

  try {
    return generateValidUrl(value);
  } catch {
    return undefined;
  }
};

export const redactSensitiveValue = (value: unknown): unknown => {
  return redactSensitiveValueInternal(value, new WeakSet());
};

const sanitizeQueryFromUrl = (url: URL): string => {
  const entries = Array.from(url.searchParams.entries());

  if (entries.length === 0) {
    return '';
  }

  return `?${entries
    .map(([key, entryValue]) => {
      return `${key}=${isSensitiveKey(key) ? REDACTED_LOG_VALUE : entryValue}`;
    })
    .join('&')}`;
};

export const sanitizeQueryForLog = (value: string | URL): string => {
  const url = toUrl(value);

  if (!url) {
    return '';
  }

  return sanitizeQueryFromUrl(url);
};

export const sanitizeUrlForLog = (value: string | URL): string => {
  const url = toUrl(value);

  if (!url) {
    return typeof value === 'string' ? value : value.toString();
  }

  return `${url.origin}${url.pathname}${sanitizeQueryFromUrl(url)}${url.hash}`;
};
